import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Card, Segmented, cx } from './ui';
import { formatCompactCurrency, formatCurrency, formatNumber, formatPercent } from '../utils/format';

// Biểu đồ của Dashboard: 1 trục, chữ dùng màu chữ thường, không tô màu theo chuỗi.
// Đã kiểm tra bảng màu bằng validate_palette (mù màu, tương phản): PLATFORM_COLOR, BUYER_COLOR.
export const PLATFORM_COLOR = { shopee: '#D9480F', tiktokshop: '#008F85', lazada: '#5B57E0' };
export const BUYER_COLOR = { returning: '#008F85', fresh: '#D97706' };

const BAR = '#D6D3D1';
const BAR_ACTIVE = '#A8A29E';
const LINE = '#1C1917';
// Sọc nghiêng: dữ liệu chưa đủ (hôm nay, tháng đang chạy)
const partialFill = (color) => `repeating-linear-gradient(135deg, ${color}, ${color} 3px, #fff 3px, #fff 5px)`;

const money = (v) => formatCompactCurrency(v);
export const METRICS = {
  validOrders: { label: 'Đơn hợp lệ', format: formatNumber, axis: formatNumber },
  profit: { label: 'Lợi nhuận', format: formatCurrency, axis: money, compact: money },
  gmv: { label: 'GMV', format: formatCurrency, axis: money, compact: money },
  buyers: { label: 'Người mua', format: formatNumber, axis: formatNumber },
  newUsers: { label: 'User mới', format: formatNumber, axis: formatNumber },
  clicks: { label: 'Lượt click', format: formatNumber, axis: formatNumber }
};

const metricOptions = (keys) => keys.map((value) => ({ value, label: METRICS[value].label }));

const shortDate = (ymd) => {
  const [, m, d] = ymd.split('-');
  return `${d}/${m}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return `T${Number(m)}/${y.slice(2)}`;
};
const monthLong = (ym) => {
  const [y, m] = ym.split('-');
  return `tháng ${Number(m)}/${y}`;
};

// null khi không có mốc so sánh
export const pctChange = (current, previous) => (previous > 0 ? (current - previous) / previous : null);

export const formatChange = (pct) => `${pct > 0 ? '+' : ''}${(pct * 100).toFixed(Math.abs(pct) < 0.1 ? 1 : 0)}%`;

// Mức thay đổi kèm mũi tên; goodWhenUp=false cho chỉ số càng thấp càng tốt. Chữ phụ giúp không phụ thuộc vào màu.
export const Change = ({ pct, goodWhenUp = true, className }) => {
  if (pct === null || pct === undefined) return <span className={cx('text-gray-400', className)}>—</span>;
  const dir = Math.abs(pct) < 0.005 ? 0 : Math.sign(pct);
  const good = dir === 0 ? null : (dir > 0) === goodWhenUp;
  const Icon = dir > 0 ? ArrowUpRight : dir < 0 ? ArrowDownRight : Minus;
  return (
    <span className={cx('tabular inline-flex items-center gap-0.5 whitespace-nowrap font-medium', good === true && 'text-emerald-700', good === false && 'text-red-600', good === null && 'text-gray-500', className)}>
      <Icon className="h-3.5 w-3.5" />
      {formatChange(pct)}
    </span>
  );
};

export const Sparkline = ({ values, color = '#78716C', className }) => {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const points = values
    .map((v, i) => `${(i / (values.length - 1)) * 100},${28 - ((v - min) / span) * 26}`)
    .join(' ');
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className={cx('h-7 w-full', className)} aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// Hộp giá trị khi rê/chạm vào một cột, giữ trong khung biểu đồ
const Tooltip = ({ index, count, children }) => {
  const left = ((index + 0.5) / count) * 100;
  return (
    <div
      className="pointer-events-none absolute top-0 z-10 w-max max-w-[14rem] rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg"
      style={left > 55 ? { right: `${100 - left}%`, marginRight: 6 } : { left: `${left}%`, marginLeft: 6 }}
    >
      {children}
    </div>
  );
};

const LegendSwatch = ({ color, line, striped, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
    {line
      ? <span className="h-0.5 w-4 rounded-full" style={{ background: color }} />
      : <span className="h-2.5 w-2.5 rounded-sm" style={{ background: striped ? partialFill(color) : color }} />}
    {children}
  </span>
);

const AxisMax = ({ value }) => (
  <span className="pointer-events-none absolute right-0 top-0 bg-white pl-1 text-[11px] text-gray-400">{value}</span>
);

// ---------- Xu hướng theo ngày ----------

const movingAverage = (values, size) => values.map((_, i) => {
  if (i < size - 1) return null;
  const slice = values.slice(i - size + 1, i + 1);
  return slice.reduce((a, b) => a + b, 0) / size;
});

const TREND_KEYS = ['validOrders', 'profit', 'gmv', 'buyers', 'newUsers', 'clicks'];

// Cột là số liệu từng ngày, đường là trung bình 7 ngày để nhìn ra xu hướng qua nhiễu theo thứ trong tuần.
// Ngày cuối là hôm nay chưa hết ngày: vẽ sọc, không tính vào đường trung bình và phép so sánh.
export const TrendChart = ({ data, previous }) => {
  const [metric, setMetric] = useState('validOrders');
  const [hover, setHover] = useState(null);
  const cfg = METRICS[metric];
  const values = data.map((d) => d[metric]);
  const prevValues = (previous || []).map((d) => d[metric]);
  const complete = values.slice(0, -1);
  const prevComplete = prevValues.slice(0, complete.length);
  const total = complete.reduce((a, b) => a + b, 0);
  const prevTotal = prevComplete.reduce((a, b) => a + b, 0);
  const change = complete.length && prevComplete.length === complete.length ? pctChange(total, prevTotal) : null;

  const ma = movingAverage([...prevValues, ...complete], 7).slice(prevValues.length);
  const max = Math.max(...values, ...ma.filter((v) => v !== null), 0);
  const points = ma
    .map((v, i) => (v === null ? null : `${((i + 0.5) / values.length) * 100},${100 - (max > 0 ? (v / max) * 100 : 0)}`))
    .filter(Boolean)
    .join(' ');
  const labelEvery = data.length > 14 ? Math.ceil(data.length / 6) : data.length > 7 ? 2 : 1;
  const active = hover !== null ? data[hover] : null;

  return (
    <Card className="p-4 md:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{cfg.label} theo ngày</h3>
          {complete.length > 0 && (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
              <span className="tabular text-xl font-semibold text-gray-900">{cfg.compact ? cfg.compact(total) : cfg.format(total)}</span>
              <span>trong {complete.length} ngày</span>
              <Change pct={change} />
              <span className="text-xs text-gray-500">so với {complete.length} ngày liền trước</span>
            </p>
          )}
        </div>
        <Segmented options={metricOptions(TREND_KEYS)} value={metric} onChange={(v) => { setMetric(v); setHover(null); }} />
      </div>

      <div className="relative mt-4" onMouseLeave={() => setHover(null)}>
        <div className="relative h-44 border-b border-gray-300">
          <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-gray-200" />
          <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200" />
          <AxisMax value={cfg.axis(max)} />
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {data.map((d, i) => {
              const h = max > 0 ? Math.max((d[metric] / max) * 100, d[metric] > 0 ? 1.5 : 0) : 0;
              const isToday = i === data.length - 1;
              const color = i === hover ? BAR_ACTIVE : BAR;
              return (
                <button
                  key={d.date}
                  type="button"
                  className="flex h-full min-w-0 flex-1 items-end focus:outline-none"
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onClick={() => setHover(i)}
                  aria-label={`${shortDate(d.date)}: ${cfg.format(d[metric])}`}
                >
                  <span className="mx-auto block w-full max-w-[3.5rem] rounded-t" style={{ height: `${h}%`, background: isToday ? partialFill(color) : color }} />
                </button>
              );
            })}
          </div>
          {points && (
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
              <polyline points={points} fill="none" stroke={LINE} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
          )}
          {active && (
            <Tooltip index={hover} count={data.length}>
              <p className="font-medium">{shortDate(active.date)}{hover === data.length - 1 ? ' (chưa hết ngày)' : ''}</p>
              <p className="tabular">{cfg.format(active[metric])}</p>
              {ma[hover] !== null && ma[hover] !== undefined && (
                <p className="tabular text-gray-300">TB 7 ngày: {cfg.format(Math.round(ma[hover]))}</p>
              )}
            </Tooltip>
          )}
        </div>
        <div className="mt-1 flex gap-[2px]">
          {data.map((d, i) => (
            <span key={d.date} className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-center text-[11px] text-gray-500">
              {(i % labelEvery === 0 || i === data.length - 1) && (data.length - 1 - i >= labelEvery / 2 || i === data.length - 1) ? shortDate(d.date) : ''}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        <LegendSwatch color={BAR}>Theo ngày</LegendSwatch>
        <LegendSwatch color={LINE} line>Trung bình 7 ngày</LegendSwatch>
        <LegendSwatch color={BAR_ACTIVE} striped>Hôm nay (chưa hết ngày)</LegendSwatch>
      </div>
    </Card>
  );
};

// ---------- Tăng trưởng theo tháng ----------

const GROWTH_KEYS = ['profit', 'gmv', 'validOrders', 'buyers', 'newUsers', 'clicks'];

const GrowthFigure = ({ label, value, children }) => (
  <div className="min-w-0">
    <p className="truncate text-xs text-gray-500">{label}</p>
    <p className="tabular mt-0.5 text-lg font-semibold text-gray-900">{value}</p>
    {children && <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-gray-500">{children}</div>}
  </div>
);

const MONTH_TO_DATE_KEYS = { validOrders: 'validOrders', profit: 'profit', gmv: 'gmv', buyers: 'buyers', newUsers: 'newUsers', clicks: 'clicks' };

export const GrowthChart = ({ growth }) => {
  const [metric, setMetric] = useState('profit');
  const [hover, setHover] = useState(null);
  const cfg = METRICS[metric];
  const { months, metrics, monthToDate } = growth;
  const summary = metrics[metric];
  const values = months.map((m) => m[metric]);
  const max = Math.max(...values, 0);
  const lastFull = months[months.length - 2];
  const previousFull = months[months.length - 3];
  const key = MONTH_TO_DATE_KEYS[metric];
  const mtdCurrent = monthToDate?.current?.[key];
  const mtdPrevious = monthToDate?.previous?.[key];
  const fmt = cfg.compact || cfg.format;
  const active = hover !== null ? months[hover] : null;

  return (
    <Card className="p-4 md:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{cfg.label} 12 tháng gần nhất</h3>
        <Segmented options={metricOptions(GROWTH_KEYS)} value={metric} onChange={(v) => { setMetric(v); setHover(null); }} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <GrowthFigure label={lastFull ? `Tháng đủ gần nhất (${monthLabel(lastFull.month)})` : 'Tháng đủ gần nhất'} value={fmt(summary.lastMonth ?? 0)}>
          <Change pct={summary.monthOverMonth} />
          {previousFull && <span>so với {monthLabel(previousFull.month)}</span>}
        </GrowthFigure>
        <GrowthFigure label="Tăng trưởng bình quân 3 tháng" value={summary.monthlyGrowth3m === null ? '—' : `${formatChange(summary.monthlyGrowth3m)}/tháng`} />
        <GrowthFigure label="Tháng này đến nay" value={fmt(mtdCurrent ?? 0)}>
          <Change pct={pctChange(mtdCurrent, mtdPrevious)} />
          <span>so với cùng kỳ tháng trước</span>
        </GrowthFigure>
      </div>

      <div className="relative mt-5" onMouseLeave={() => setHover(null)}>
        <div className="relative h-44 border-b border-gray-300">
          <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-gray-200" />
          <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200" />
          <AxisMax value={cfg.axis(max)} />
          <div className="absolute inset-0 flex items-end gap-1 md:gap-2">
            {months.map((m, i) => {
              const h = max > 0 ? Math.max((m[metric] / max) * 100, m[metric] > 0 ? 1.5 : 0) : 0;
              const color = i === hover ? '#44403C' : '#78716C';
              return (
                <button
                  key={m.month}
                  type="button"
                  className="flex h-full min-w-0 flex-1 items-end focus:outline-none"
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onClick={() => setHover(i)}
                  aria-label={`${monthLong(m.month)}: ${cfg.format(m[metric])}`}
                >
                  <span className="block w-full rounded-t" style={{ height: `${h}%`, background: m.partial ? partialFill(color) : color }} />
                </button>
              );
            })}
          </div>
          {active && (
            <Tooltip index={hover} count={months.length}>
              <p className="font-medium">{monthLong(active.month)}{active.partial ? ' (đang chạy)' : ''}</p>
              <p className="tabular">{cfg.format(active[metric])}</p>
              {hover > 0 && !active.partial && pctChange(active[metric], months[hover - 1][metric]) !== null && (
                <p className="tabular text-gray-300">{formatChange(pctChange(active[metric], months[hover - 1][metric]))} so với tháng trước</p>
              )}
            </Tooltip>
          )}
        </div>
        <div className="mt-1 flex gap-1 md:gap-2">
          {months.map((m, i) => (
            <span key={m.month} className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-center text-[11px] text-gray-500">
              <span className={i % 2 === 1 ? 'hidden sm:inline' : ''}>{monthLabel(m.month)}</span>
            </span>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        <LegendSwatch color="#78716C">Tháng đủ</LegendSwatch>
        <LegendSwatch color="#78716C" striped>Tháng đang chạy</LegendSwatch>
      </div>

      <details className="mt-4 border-t border-gray-200 pt-3">
        <summary className="cursor-pointer text-sm font-medium text-gray-700">Bảng số liệu từng tháng</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500">
                {['Tháng', 'Đơn hợp lệ', 'GMV', 'Lợi nhuận', 'Người mua', 'User mới', 'Tổng user', 'Tỷ lệ hủy', 'Chuyển đổi'].map((h, i) => (
                  <th key={h} className={cx('whitespace-nowrap py-2 pr-4 font-medium', i === 0 ? 'text-left' : 'text-right')}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...months].reverse().map((m) => (
                <tr key={m.month}>
                  <td className="whitespace-nowrap py-2 pr-4 font-medium text-gray-900">{monthLabel(m.month)}{m.partial ? ' *' : ''}</td>
                  <td className="py-2 pr-4 text-right">{formatNumber(m.validOrders)}</td>
                  <td className="py-2 pr-4 text-right">{money(m.gmv)}</td>
                  <td className="py-2 pr-4 text-right font-medium text-gray-900">{money(m.profit)}</td>
                  <td className="py-2 pr-4 text-right">{formatNumber(m.buyers)}</td>
                  <td className="py-2 pr-4 text-right">{formatNumber(m.newUsers)}</td>
                  <td className="py-2 pr-4 text-right">{formatNumber(m.totalUsers)}</td>
                  <td className="py-2 pr-4 text-right">{formatPercent(m.cancelRate)}</td>
                  <td className="py-2 pr-4 text-right">{formatPercent(m.conversionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500">* Tháng đang chạy, chưa đủ ngày.</p>
        </div>
      </details>
    </Card>
  );
};

// ---------- Người mua mới và quay lại ----------

const RatioTile = ({ label, value, previous, hint, goodWhenUp = true }) => (
  <div className="min-w-0">
    <p className="truncate text-xs text-gray-500">{label}</p>
    <p className="tabular mt-0.5 text-xl font-semibold text-gray-900">{value === null || value === undefined ? '—' : formatPercent(value)}</p>
    {value !== null && value !== undefined && previous !== null && previous !== undefined && (
      <RatioDelta diff={value - previous} goodWhenUp={goodWhenUp} />
    )}
    <p className="mt-1 text-xs leading-relaxed text-gray-500">{hint}</p>
  </div>
);

const RatioDelta = ({ diff, goodWhenUp }) => {
  const points = diff * 100;
  const flat = Math.abs(points) < 0.05;
  const good = flat ? null : (points > 0) === goodWhenUp;
  const Icon = flat ? Minus : points > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <p className={cx('tabular mt-0.5 inline-flex items-center gap-0.5 text-xs font-medium', good === true && 'text-emerald-700', good === false && 'text-red-600', good === null && 'text-gray-500')}>
      <Icon className="h-3.5 w-3.5" />
      {points > 0 ? '+' : ''}{points.toFixed(1)} điểm
    </p>
  );
};

export const BuyersChart = ({ months }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(...months.map((m) => m.buyers), 0);
  const last = months[months.length - 2];
  const prev = months[months.length - 3];
  const active = hover !== null ? months[hover] : null;
  return (
    <Card className="p-4 md:p-5">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Người mua mới và quay lại</h3>
          <div className="relative mt-4" onMouseLeave={() => setHover(null)}>
            <div className="relative h-44 border-b border-gray-300">
              <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-gray-200" />
              <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-gray-200" />
              <AxisMax value={formatNumber(max)} />
              <div className="absolute inset-0 flex items-end gap-1 md:gap-2">
                {months.map((m, i) => {
                  const scale = max > 0 ? 100 / max : 0;
                  const fill = (color) => (m.partial ? partialFill(color) : color);
                  return (
                    <button
                      key={m.month}
                      type="button"
                      className={cx('flex h-full min-w-0 flex-1 flex-col justify-end gap-[2px] focus:outline-none', hover !== null && hover !== i && 'opacity-60')}
                      onMouseEnter={() => setHover(i)}
                      onFocus={() => setHover(i)}
                      onClick={() => setHover(i)}
                      aria-label={`${monthLong(m.month)}: ${formatNumber(m.returningBuyers)} quay lại, ${formatNumber(m.newBuyers)} mới`}
                    >
                      <span className="block w-full rounded-t" style={{ height: `${m.newBuyers * scale}%`, background: fill(BUYER_COLOR.fresh) }} />
                      <span className="block w-full" style={{ height: `${m.returningBuyers * scale}%`, background: fill(BUYER_COLOR.returning) }} />
                    </button>
                  );
                })}
              </div>
              {active && (
                <Tooltip index={hover} count={months.length}>
                  <p className="font-medium">{monthLong(active.month)}{active.partial ? ' (đang chạy)' : ''}</p>
                  <p className="tabular">{formatNumber(active.newBuyers)} mới · {formatNumber(active.returningBuyers)} quay lại</p>
                  <p className="tabular text-gray-300">Tổng {formatNumber(active.buyers)} người mua</p>
                </Tooltip>
              )}
            </div>
            <div className="mt-1 flex gap-1 md:gap-2">
              {months.map((m, i) => (
                <span key={m.month} className="min-w-0 flex-1 overflow-visible whitespace-nowrap text-center text-[11px] text-gray-500">
                  <span className={i % 2 === 1 ? 'hidden sm:inline' : ''}>{monthLabel(m.month)}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <LegendSwatch color={BUYER_COLOR.fresh}>Mua lần đầu</LegendSwatch>
            <LegendSwatch color={BUYER_COLOR.returning}>Quay lại</LegendSwatch>
          </div>
        </div>

        {last && (
          <div className="grid grid-cols-2 content-start gap-x-4 gap-y-5 border-t border-gray-200 pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <p className="col-span-2 text-xs font-medium text-gray-600">{`Tháng đủ gần nhất (${monthLabel(last.month)}), so với tháng trước`}</p>
            <RatioTile label="Giữ chân" value={last.retentionRate} previous={prev?.retentionRate} hint="Người mua tháng trước vẫn mua tháng này." />
            <RatioTile label="Tỷ lệ quay lại" value={last.returningRate} previous={prev?.returningRate} hint="Người mua trong tháng đã từng mua trước đó." />
            <RatioTile label="Kích hoạt" value={last.activationRate} previous={prev?.activationRate} hint="User đăng ký trong tháng đã có đơn. Tháng gần đây còn tăng thêm." />
            <div className="min-w-0">
              <p className="truncate text-xs text-gray-500">Tổng người dùng</p>
              <p className="tabular mt-0.5 text-xl font-semibold text-gray-900">{formatNumber(last.totalUsers)}</p>
              <p className="tabular mt-0.5 text-xs text-gray-500">+{formatNumber(last.newUsers)} trong tháng</p>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

// ---------- Theo sàn ----------

const PLATFORM_LABEL = { shopee: 'Shopee', tiktokshop: 'TikTok Shop', lazada: 'Lazada' };

export const PlatformBreakdown = ({ platforms, previous }) => {
  const prevOf = (key) => (previous || []).find((p) => p.platform === key) || {};
  const totalProfit = platforms.reduce((s, p) => s + Math.max(p.profit, 0), 0);
  return (
    <Card className="overflow-hidden">
      <div className="p-4 pb-3 md:px-5">
        <h3 className="text-sm font-semibold text-gray-900">Hiệu quả theo sàn</h3>
        <p className="mt-0.5 text-xs text-gray-500">Mũi tên là thay đổi so với kỳ trước.</p>
        {totalProfit > 0 && (
          <>
            <div className="mt-3 flex h-3 gap-[2px] overflow-hidden rounded">
              {platforms.filter((p) => p.profit > 0).map((p) => (
                <div key={p.platform} title={`${PLATFORM_LABEL[p.platform]}: ${formatPercent(p.profit / totalProfit, 0)}`} style={{ width: `${(p.profit / totalProfit) * 100}%`, background: PLATFORM_COLOR[p.platform] }} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {platforms.map((p) => (
                <LegendSwatch key={p.platform} color={PLATFORM_COLOR[p.platform]}>
                  {PLATFORM_LABEL[p.platform]} {formatPercent(Math.max(p.profit, 0) / totalProfit, 0)} lợi nhuận
                </LegendSwatch>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Mobile: mỗi sàn một khối, desktop: bảng so sánh */}
      <div className="divide-y divide-gray-100 border-t border-gray-200 md:hidden">
        {platforms.map((p) => {
          const prev = prevOf(p.platform);
          return (
            <div key={p.platform} className="px-4 py-3">
              <p className="text-sm font-medium text-gray-900">{PLATFORM_LABEL[p.platform]}</p>
              <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                {[
                  ['Lợi nhuận', money(p.profit), pctChange(p.profit, prev.profit)],
                  ['Đơn hợp lệ', formatNumber(p.validOrders), pctChange(p.validOrders, prev.validOrders)],
                  ['GMV', money(p.gmv), pctChange(p.gmv, prev.gmv)]
                ].map(([label, value, pct]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-gray-500">{label}</dt>
                    <dd className="tabular mt-0.5 font-semibold text-gray-900">{value}</dd>
                    <dd className="mt-0.5"><Change pct={pct} /></dd>
                  </div>
                ))}
              </dl>
              <p className="tabular mt-2 text-xs text-gray-500">
                {formatNumber(p.clicks)} click · chuyển đổi {formatPercent(p.conversionRate)} · hủy {formatPercent(p.cancelRate)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto border-t border-gray-200 md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              {['Sàn', 'Lợi nhuận', 'GMV', 'Đơn hợp lệ', 'Người mua', 'Lượt click', 'Chuyển đổi', 'Tỷ lệ hủy'].map((h, i) => (
                <th key={h} className={cx('whitespace-nowrap px-4 py-2.5 font-medium md:first:pl-5 md:last:pr-5', i === 0 ? 'text-left' : 'text-right')}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {platforms.map((p) => {
              const prev = prevOf(p.platform);
              const cell = (value, pct) => (
                <td className="whitespace-nowrap px-4 py-2.5 text-right">
                  <span className="text-gray-900">{value}</span>
                  <span className="ml-2 inline-block w-16 text-left text-xs"><Change pct={pct} /></span>
                </td>
              );
              return (
                <tr key={p.platform}>
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-gray-900 md:pl-5">
                    <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm" style={{ background: PLATFORM_COLOR[p.platform] }} />
                    {PLATFORM_LABEL[p.platform]}
                  </td>
                  {cell(money(p.profit), pctChange(p.profit, prev.profit))}
                  {cell(money(p.gmv), pctChange(p.gmv, prev.gmv))}
                  {cell(formatNumber(p.validOrders), pctChange(p.validOrders, prev.validOrders))}
                  {cell(formatNumber(p.buyers), pctChange(p.buyers, prev.buyers))}
                  {cell(formatNumber(p.clicks), pctChange(p.clicks, prev.clicks))}
                  <td className="px-4 py-2.5 text-right text-gray-900">{formatPercent(p.conversionRate)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-900 md:pr-5">{formatPercent(p.cancelRate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
